import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { debounce } from 'lodash-decorators';
import { SelectItem } from 'primeng/api';
import { DatagridService, NotificationService, PagedResultGeneric, PagedSearch, TranslationService } from 'src/app/shared';
import { LazySelectableTableComponent } from 'src/app/shared/primeng/abstract-classes/lazy-selectable-table.component';
import { ClassTypeService } from '../class-type/shared/class-type.service';
import { PostTransactionsSearchDto } from '../shared/dto/post-transactions-search.dto';
import { FinancialServiceService } from '../shared/financial.service';
import { AccountingPackageService } from '../shared/accounting-package/accounting-package.service';
import { AccountingPackage } from '../shared/accounting-package/accounting-package.model';
import { PostTransactionSummaryAccountChangeDto } from '../shared/dto/unposted-transaction-summary-account-change.dto';
import { AccountDto } from '../shared/accounting-package/dto/account.dto';
import { UnpostedTransactionResultDto } from '../shared/dto/unposted-transaction-result.dto';
import { PostTransactionSummaryOptionsDto } from '../shared/dto/unposted-transaction-summary-options.dto';
import { DebitCreditAccountValidationResultDto } from '../shared/dto/debit-credit-account.validation-result.dto';

@Component({
  selector: 'wl-journal-entry-details',
  templateUrl: './journal-entry-details.component.html',
  styleUrls: ['./journal-entry-details.component.less'],
    standalone: false
})
export class JournalEntryDetailsComponent
  extends LazySelectableTableComponent<PostTransactionsSearchDto, UnpostedTransactionResultDto>
  implements OnInit
{
  @Input() searchQueryParams: PostTransactionsSearchDto;
  @Input() accountChanges: Array<PostTransactionSummaryAccountChangeDto>;
  @Output() accountChanged = new EventEmitter<PostTransactionSummaryAccountChangeDto>();
  hideShowAll: boolean = false;
  hideClassColumn: boolean = true;
  transactionTypes: SelectItem[] = [
    { label: 'Adjustments', value: 'Adjustment' },
    { label: 'Invoice Line Item', value: 'Invoice Line Item' },
    { label: 'Payments', value: 'Invoice Payment' },
    { label: 'Unapplied Payments', value: 'Unapplied Payment' },
  ];
  showValidationError: boolean = true;
  mismatchedCompositeKeys: Array<string> = [];
  classTypes: Array<SelectItem>;
  accountSelectItems: Array<SelectItem>;
  accounts: Array<AccountDto>;
  initTimeStamp = new Date().getTime();

  constructor(
    datagridService: DatagridService,
    private _financialService: FinancialServiceService,
    private _classTypeService: ClassTypeService,
    private _notificationService: NotificationService,
    private _translationService: TranslationService,
    private _accountingPackageService: AccountingPackageService
  ) {
    super('CompositeKey', datagridService);
    this.searchParams = {} as PostTransactionsSearchDto;
  }

  ngOnInit(): void {
    this.searchParams.PageNumber = 1;
    this.searchParams.PageSize = 10;

    this._classTypeService.getClassTypes({ PageNumber: 0, PageSize: 0 } as PagedSearch).then((data) => {
      this.classTypes = data.Result.map((x) => {
        return { label: x.ClassType, value: x.ClassType } as SelectItem;
      });
    });
  }

  init() {
    this.searchParams.TranType = this.searchQueryParams.TranType;
    this.searchParams.EndDate = this.searchQueryParams.EndDate;
    this.searchParams.StartDate = this.searchQueryParams.StartDate;
    this.searchParams.AccountingPackageID = this.searchQueryParams.AccountingPackageID;
    this.searchParams.ShowUnpostedTransaction = this.searchQueryParams.ShowUnpostedTransaction;
    this.searchParams.TranCodes = this.searchQueryParams.TranCodes;
    this.selectAll = true;
    this.getAccounts();
  }

  softReset() {
    this.searchParams.TranType = this.searchQueryParams.TranType;
    this.searchParams.EndDate = this.searchQueryParams.EndDate;
    this.searchParams.StartDate = this.searchQueryParams.StartDate;
    this.searchParams.AccountingPackageID = this.searchQueryParams.AccountingPackageID;
    this.searchParams.ShowUnpostedTransaction = this.searchQueryParams.ShowUnpostedTransaction;
    this.searchParams.TranCodes = this.searchQueryParams.TranCodes;
    this.showValidationError = true;
    this.selectAll = true;
    this.setHideClassColumn();
    this.getAccounts();
  }

  fetchResults(): Promise<PagedResultGeneric<UnpostedTransactionResultDto>> {
    let searchOptions = this.searchParams as PostTransactionSummaryOptionsDto;
    searchOptions.AccountChanges = this.accountChanges;
    return this._financialService
      .validateUnpostedTransactions(searchOptions)
      .then((result: DebitCreditAccountValidationResultDto) => {
        this.mismatchedCompositeKeys = result.MismatchedCompositeKeys ?? [];
        if (this.showValidationError) {
          if (result.HasAccountingPackageMismatch) {
            this._notificationService.translateWarning(
              'revenue.post-transactions.post-transactions-search.accounting-package-mismatch-warning'
            );
          }
          if (result.HasEmptyAccounts) {
            this._notificationService.translateWarning(
              'revenue.post-transactions.post-transactions-search.missing-debit-credit-account-error'
            );
          }
          this.showValidationError = false;
        }
        return this.searchUnpostedTransactions();
      })
      .finally(() => {
        this.setAccountChanges();
        this.searchResult?.Result.forEach((t) => this.setAccountingPackageMismatch(t));
        this.loading = false;
      });
  }

  searchUnpostedTransactions(): Promise<PagedResultGeneric<UnpostedTransactionResultDto>> {
    return this._financialService
      .searchUnpostedTransactions(this.searchParams)
      .then((data) => {
        this.searchResult = data;
        return data;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  async showAll() {
    this.hideShowAll = true;
    this.searchParams.PageNumber = 1;
    this.searchParams.PageSize = 999999;
    this.loading = true;
    await this.fetchResults();
    this.selectAll = true;
    this.onSelectAllChange();
  }

  @debounce(500)
  lazySearch() {
    // date selector is triggering change even when model is set. this is a hacky way to keep that from happening.
    if (new Date().getTime() - this.initTimeStamp < 1000) {
      return;
    }
    this.search();
    if (!this.searchParams.InvoiceNum) {
      this.searchParams.InvoiceNum = null;
    }
  }

  setHideClassColumn() {
    this._accountingPackageService.get(this.searchParams.AccountingPackageID).then((accountingPackage: AccountingPackage) => {
      if (accountingPackage.PackageType != 1 && accountingPackage.PackageType != 4) {
        this.hideClassColumn = true;
      } else {
        this.hideClassColumn = false;
      }
    });
  }

  getAccounts() {
    this._financialService.getAccountsByAccountingPackage(this.searchParams.AccountingPackageID).then((accounts) => {
      this.accounts = accounts;
      this.accountSelectItems = accounts.map<SelectItem>((account) => {
        return { label: `${account.AccountNum} ${account.Descr}`, value: account.AccountNum };
      });
    });
  }

  changeAccount(transaction: UnpostedTransactionResultDto) {
    let debitDescr = this.accounts.find((x) => x.AccountNum == transaction.DebitAccount.toString())?.Descr;
    let creditDescr = this.accounts.find((x) => x.AccountNum == transaction.CreditAccount.toString())?.Descr;

    let accountChange = {
      CompositeKey: transaction.CompositeKey,
      NewDebitAccount: transaction.DebitAccount,
      NewCreditAccount: transaction.CreditAccount,
      NewDebitDescr: debitDescr,
      NewCreditDescr: creditDescr,
    } as PostTransactionSummaryAccountChangeDto;

    this.accountChanged.emit(accountChange);
    this.setAccountingPackageMismatch(transaction);
  }

  private setAccountingPackageMismatch(transaction: UnpostedTransactionResultDto) {
    if (
      !this.mismatchedCompositeKeys.includes(transaction.CompositeKey) &&
      !this.accountChanges.some((x) => x.CompositeKey == transaction.CompositeKey)
    ) {
      transaction.HasAccountingPackageMismatch = false;
      return;
    }
    const debitExists = this.accounts?.some((a) => a.AccountNum == transaction.DebitAccount.toString());
    const creditExists = this.accounts?.some((a) => a.AccountNum == transaction.CreditAccount.toString());
    transaction.HasAccountingPackageMismatch = !(debitExists && creditExists);
  }

  // Overwrite the results from the search with the account changes
  private setAccountChanges() {
    this.accountChanges.forEach((element) => {
      let transaction = this.searchResult.Result.find((x) => x.CompositeKey == element.CompositeKey);
      if (transaction) {
        transaction.DebitAccount = element.NewDebitAccount;
        transaction.CreditAccount = element.NewCreditAccount;
        transaction.DebitDescr = element.NewDebitDescr;
        transaction.CreditDescr = element.NewCreditDescr;
      }
    });
  }
}
